import axios from "axios";
import { AppResponse } from "../models/Response";
import { PIIPatternResponse, UpdatePatternRequest, PatternGroupsResponse } from "../models/PIIPattern";

export class PIIPatternService {
  private static readonly PII_BASE_URL = "https://pii.mylavni.com/api/v1";

  /**
   * Get all PII pattern groups
   */
  public static async getAllPatternGroups(): Promise<AppResponse<PatternGroupsResponse>> {
    const url = `${this.PII_BASE_URL}/pattern-groups`;

    try {
      const response = await axios.get<PatternGroupsResponse>(url, {
        headers: {
          'accept': 'application/json'
        }
      });

      console.log('Pattern Groups API response:', response);
      console.log('Response data:', response.data);
      console.log('Full response object:', response);
      console.log('Response keys:', Object.keys(response));

      // Check if the data is in response directly or response.data
      let patternGroupsData: PatternGroupsResponse;

      if (response.data && response.data.groups) {
        patternGroupsData = response.data;
      } else if ((response as any).groups) {
        // If response itself has groups property
        patternGroupsData = { groups: (response as any).groups };
      } else {
        // Fallback - assume response is the data
        patternGroupsData = response as any;
      }

      console.log('Final pattern groups data:', patternGroupsData);

      return {
        success: true,
        data: patternGroupsData,
        message: "Pattern groups retrieved successfully",
        length: 0,
        createdAt: () => new Date(),
        errorData: patternGroupsData
      };
    } catch (error: any) {
      return {
        success: false,
        data: { groups: [] } as PatternGroupsResponse,
        message: error.response?.data?.message || "Failed to retrieve pattern groups",
        length: 0,
        createdAt: () => new Date(),
        errorData: { groups: [] } as PatternGroupsResponse
      };
    }
  }

  /**
   * Create new pattern group
   */
  public static async createPatternGroup(name: string, description: string = ""): Promise<AppResponse<any>> {
    const url = `${this.PII_BASE_URL}/pattern-groups`;

    try {
      const response = await axios.post(url, {
        name,
        description
      }, {
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      console.log('Create Pattern Group API response:', response);

      return {
        success: true,
        data: response.data,
        message: "Pattern group created successfully",
        length: 0,
        createdAt: () => new Date(),
        errorData: response.data
      };
    } catch (error: any) {
      return {
        success: false,
        data: null,
        message: error.response?.data?.message || "Failed to create pattern group",
        length: 0,
        createdAt: () => new Date(),
        errorData: error.response?.data || error.message
      };
    }
  }

  /**
   * Delete pattern group
   */
  public static async deletePatternGroup(groupId: string): Promise<AppResponse<any>> {
    const url = `${this.PII_BASE_URL}/pattern-groups/${groupId}`;

    try {
      const response = await axios.delete(url, {
        headers: {
          'accept': 'application/json'
        }
      });

      console.log('Delete Pattern Group API response:', response);

      return {
        success: true,
        data: response.data,
        message: "Pattern group deleted successfully",
        length: 0,
        createdAt: () => new Date(),
        errorData: response.data
      };
    } catch (error: any) {
      return {
        success: false,
        data: null,
        message: error.response?.data?.message || "Failed to delete pattern group",
        length: 0,
        createdAt: () => new Date(),
        errorData: error.response?.data || error.message
      };
    }
  }

  /**
   * Update pattern group
   */
  public static async updatePatternGroup(groupId: string, name: string, description: string = ""): Promise<AppResponse<any>> {
    const url = `${this.PII_BASE_URL}/pattern-groups/${groupId}`;

    try {
      const response = await axios.put(url, {
        name,
        description
      }, {
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      console.log('Update Pattern Group API response:', response);

      return {
        success: true,
        data: response.data,
        message: "Pattern group updated successfully",
        length: 0,
        createdAt: () => new Date(),
        errorData: response.data
      };
    } catch (error: any) {
      return {
        success: false,
        data: null,
        message: error.response?.data?.message || "Failed to update pattern group",
        length: 0,
        createdAt: () => new Date(),
        errorData: error.response?.data || error.message
      };
    }
  }

  /**
   * Create pattern in group
   */
  public static async createPatternInGroup(groupId: string, label: string, pattern: string): Promise<AppResponse<any>> {
    const url = `${this.PII_BASE_URL}/pattern-groups/${groupId}/patterns`;

    try {
      const response = await axios.post(url, {
        label,
        pattern
      }, {
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      console.log('Create Pattern in Group API response:', response);

      return {
        success: true,
        data: response.data,
        message: "Pattern created successfully",
        length: 0,
        createdAt: () => new Date(),
        errorData: response.data
      };
    } catch (error: any) {
      return {
        success: false,
        data: null,
        message: error.response?.data?.message || "Failed to create pattern",
        length: 0,
        createdAt: () => new Date(),
        errorData: error.response?.data || error.message
      };
    }
  }

  /**
   * Delete pattern from group
   */
  public static async deletePatternFromGroup(groupId: string, patternLabel: string): Promise<AppResponse<any>> {
    const url = `${this.PII_BASE_URL}/pattern-groups/${groupId}/patterns/${patternLabel}`;

    try {
      const response = await axios.delete(url, {
        headers: {
          'accept': 'application/json'
        }
      });

      console.log('Delete Pattern from Group API response:', response);

      return {
        success: true,
        data: response.data,
        message: "Pattern deleted successfully",
        length: 0,
        createdAt: () => new Date(),
        errorData: response.data
      };
    } catch (error: any) {
      return {
        success: false,
        data: null,
        message: error.response?.data?.message || "Failed to delete pattern",
        length: 0,
        createdAt: () => new Date(),
        errorData: error.response?.data || error.message
      };
    }
  }

  /**
   * Update patterns in group
   */
  public static async updatePatternsInGroup(groupId: string, patternLabel: string, patterns: string[]): Promise<AppResponse<any>> {
    const url = `${this.PII_BASE_URL}/pattern-groups/${groupId}/patterns/${patternLabel}`;

    try {
      const response = await axios.put(url, {
        patterns
      }, {
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      console.log('Update Patterns in Group API response:', response);

      return {
        success: true,
        data: response.data,
        message: "Patterns updated successfully",
        length: 0,
        createdAt: () => new Date(),
        errorData: response.data
      };
    } catch (error: any) {
      return {
        success: false,
        data: null,
        message: error.response?.data?.message || "Failed to update patterns",
        length: 0,
        createdAt: () => new Date(),
        errorData: error.response?.data || error.message
      };
    }
  }

  /**
   * Get all PII patterns (legacy method)
   */
  public static async getAllPatterns(): Promise<AppResponse<PIIPatternResponse>> {
    const url = `${this.PII_BASE_URL}/patterns`;
    
    try {
      const response = await axios.get<PIIPatternResponse>(url, {
        headers: {
          'accept': 'application/json'
        }
      });

      console.log('Raw API response:', response);
      console.log('Response data:', response.data);

      // Nếu response.data undefined, lấy trực tiếp từ response
      const actualData = response.data || response;
      console.log('Actual data to use:', actualData);

      return {
        success: true,
        data: actualData,
        message: "Patterns retrieved successfully",
        length: 0,
        createdAt: () => new Date(),
        errorData: actualData
      };
    } catch (error: any) {
      return {
        success: false,
        data: { patterns: {} } as PIIPatternResponse,
        message: error.response?.data?.message || "Failed to retrieve patterns",
        length: 0,
        createdAt: () => new Date(),
        errorData: { patterns: {} } as PIIPatternResponse
      };
    }
  }

  /**
   * Update patterns for a specific label
   */
  public static async updatePattern(label: string, data: UpdatePatternRequest): Promise<AppResponse<any>> {
    const url = `${this.PII_BASE_URL}/patterns/${label}`;
    
    try {
      const response = await axios.put(url, data, {
        headers: {
          'Content-Type': 'application/json',
          'accept': 'application/json'
        }
      });
      
      return {
        success: true,
        data: response.data,
        message: "Pattern updated successfully",
        length: 0,
        createdAt: () => new Date(),
        errorData: response.data
      };
    } catch (error: any) {
      return {
        success: false,
        data: null,
        message: error.response?.data?.message || "Failed to update pattern",
        length: 0,
        createdAt: () => new Date(),
        errorData: null
      };
    }
  }

  /**
   * Delete all patterns for a specific label
   */
  public static async deletePattern(label: string): Promise<AppResponse<any>> {
    const url = `${this.PII_BASE_URL}/patterns/${label}`;

    try {
      const response = await axios.delete(url, {
        headers: {
          'accept': 'application/json'
        }
      });

      return {
        success: true,
        data: response.data,
        message: "Pattern deleted successfully",
        length: 0,
        createdAt: () => new Date(),
        errorData: response.data
      };
    } catch (error: any) {
      return {
        success: false,
        data: null,
        message: error.response?.data?.message || "Failed to delete pattern",
        length: 0,
        createdAt: () => new Date(),
        errorData: null
      };
    }
  }

  /**
   * Add new pattern
   */
  public static async addPattern(label: string, pattern: string): Promise<AppResponse<any>> {
    const url = `${this.PII_BASE_URL}/patterns`;

    try {
      const response = await axios.post(url, {
        label,
        pattern
      }, {
        headers: {
          'Content-Type': 'application/json',
          'accept': 'application/json'
        }
      });

      return {
        success: true,
        data: response.data,
        message: "Pattern added successfully",
        length: 0,
        createdAt: () => new Date(),
        errorData: response.data
      };
    } catch (error: any) {
      return {
        success: false,
        data: null,
        message: error.response?.data?.message || "Failed to add pattern",
        length: 0,
        createdAt: () => new Date(),
        errorData: null
      };
    }
  }

  /**
   * Get all function group mappings
   */
  public static async getFunctionGroupMappings(): Promise<AppResponse<any>> {
    const url = `${this.PII_BASE_URL}/function-group-mappings`;
    console.log('getFunctionGroupMappings - URL:', url);

    try {
      const response = await axios.get(url, {
        headers: {
          'accept': 'application/json'
        }
      });

      console.log('getFunctionGroupMappings - Raw response:', response);
      console.log('getFunctionGroupMappings - Response data:', response.data);

      // Handle axios response data like other methods in the codebase
      const actualData = response.data || response;
      console.log('getFunctionGroupMappings - Actual data to use:', actualData);

      return {
        success: true,
        data: actualData,
        message: "Function mappings retrieved successfully",
        length: 0,
        createdAt: () => new Date(),
        errorData: actualData
      };
    } catch (error: any) {
      console.error('getFunctionGroupMappings - Error:', error);
      console.error('getFunctionGroupMappings - Error response:', error.response);
      return {
        success: false,
        data: null,
        message: error.response?.data?.message || "Failed to retrieve function mappings",
        length: 0,
        createdAt: () => new Date(),
        errorData: null
      };
    }
  }

  /**
   * Update function group mapping
   */
  public static async updateFunctionGroupMapping(functionName: string, groupPatternId: string): Promise<AppResponse<any>> {
    const url = `${this.PII_BASE_URL}/function-group-mappings/${functionName}`;

    try {
      const response = await axios.put(url, {
        group_pattern_id: groupPatternId
      }, {
        headers: {
          'Content-Type': 'application/json',
          'accept': 'application/json'
        }
      });

      return {
        success: true,
        data: response.data,
        message: `Function mapping for ${functionName} updated successfully`,
        length: 0,
        createdAt: () => new Date(),
        errorData: response.data
      };
    } catch (error: any) {
      return {
        success: false,
        data: null,
        message: error.response?.data?.message || `Failed to update function mapping for ${functionName}`,
        length: 0,
        createdAt: () => new Date(),
        errorData: null
      };
    }
  }
}
